// Alt visualisation — horizontal grouped bars (2026 vs 2022) per
// district. Sibling to TurnoutCubeGrid: same data, same wave-
// selection rule, different shape. Selected via the Tiles/Bars
// toggle owned by TurnoutCubeSection.
//
// Behaviour:
//   · One row per district (D1–D13), sorted by district number.
//   · Two bars per row: 2026 (live, coloured by 2022 margin —
//     matches the cubes' palette) + 2022 (muted gray for visual
//     subordination).
//   · Auto-picks wave (2pm vs closing) the same way the cubes do:
//     closing the moment any district has a 10pm reading, else
//     2pm. The two viz read the same figure as each other.
//   · National-average vertical reference line so the eye can
//     pick out who's above / below the country at a glance.
//   · Honest-empty: rows with no 2026 figure render only the 2022
//     bar — the page never lies that 2026 = 0.
//
// No own header/title/lede — the parent (TurnoutCubeSection on
// the homepage; section wrapper on /turnout) owns the section
// chrome so the two viz can swap cleanly under ONE heading.

import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MAP_DATA, marginToColor } from '../../lib/buildMapPaths';
import {
  TURNOUT_HISTORY,
  NATIONAL_TURNOUT_HISTORY,
  type ElectionYear,
} from '../../data/turnoutHistory';
import { useElectionDayTurnout } from '../../lib/useElectionDayTurnout';
import historyJson from '../../data/district_turnout_history.json';
import FeatureSection from '../FeatureSection';
import TurnoutShareButton from '../Share/TurnoutShareButton';
import './TurnoutAltBars.css';

const CLOSE_HISTORY = historyJson as Record<string, Record<string, number>>;

type Wave = 'pct_2pm' | 'pct_close';

interface DistrictMeta {
  id: number;
  color: string;
}

const DISTRICTS: DistrictMeta[] = MAP_DATA.paths
  .map((p) => ({
    id: p.id,
    color: marginToColor(p.properties.results_2022.margin_pp),
  }))
  .sort((a, b) => a.id - b.id);

function read2022(districtId: number, wave: Wave): number | null {
  if (wave === 'pct_2pm') {
    const v = TURNOUT_HISTORY[2022 as ElectionYear]?.[districtId]?.pct_2pm;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  }
  const fromJson = CLOSE_HISTORY[`District ${districtId}`]?.['2022'];
  if (typeof fromJson === 'number' && Number.isFinite(fromJson)) return fromJson;
  const fromHist = TURNOUT_HISTORY[2022 as ElectionYear]?.[districtId]?.pct_close;
  return typeof fromHist === 'number' && Number.isFinite(fromHist) ? fromHist : null;
}

interface ChartRow {
  name: string;
  color: string;
  t2026: number | null;
  t2022: number | null;
}

export default function TurnoutAltBars() {
  const { data: turnout } = useElectionDayTurnout();

  // Match the cubes' wave-picking rule: closing the moment any
  // district has a 10pm reading, else 2pm. Keeps the two viz
  // showing the same figure as each other.
  const wave: Wave = useMemo(() => {
    const has10pm = Object.values(turnout.districts ?? {}).some(
      (d) => d?.pct_10pm !== null && d?.pct_10pm !== undefined,
    );
    return has10pm ? 'pct_close' : 'pct_2pm';
  }, [turnout]);

  const rows = useMemo<ChartRow[]>(() => {
    return DISTRICTS.map((d) => {
      const live = turnout.districts[`District ${d.id}`];
      const t2026 =
        wave === 'pct_close' ? (live?.pct_10pm ?? null) : (live?.pct_2pm ?? null);
      return {
        name: `D${d.id}`,
        color: d.color,
        t2026,
        t2022: read2022(d.id, wave),
      };
    });
  }, [wave, turnout]);

  const anyLive = rows.some((r) => r.t2026 !== null);
  const nationalRec = NATIONAL_TURNOUT_HISTORY[2022 as ElectionYear];
  const national2022 = wave === 'pct_close' ? nationalRec.pct_close : nationalRec.pct_2pm;

  // X-domain matched to the wave so the bars use most of the
  // canvas. Closing turnouts cluster 80–100%; 2pm clusters 30–65%.
  const xDomain: [number, number] = wave === 'pct_close' ? [70, 100] : [25, 70];
  const waveLabel = wave === 'pct_close' ? 'Final' : '2pm';

  return (
    <div className="turnout-alt-bars">
      {/* Share-as-PNG affordance — flag-gated by `share_buttons` so
          editorial keeps the master kill switch. Sits at top-right of
          the chart so the eye finds it after reading the data. */}
      <FeatureSection sectionKey="share_buttons" label="Share buttons">
        <div className="turnout-alt-bars__share-row">
          <TurnoutShareButton label="Share chart" variant="chip" />
        </div>
      </FeatureSection>
      <div className="turnout-alt-bars__chart">
        <ResponsiveContainer width="100%" height={620}>
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 24, right: 32, bottom: 8, left: 8 }}
            barGap={2}
            barCategoryGap={4}
            maxBarSize={28}
          >
            <CartesianGrid stroke="#e8e8e8" strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={xDomain}
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={{ stroke: '#999' }}
              axisLine={{ stroke: '#999' }}
              tickFormatter={(v: number) => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 13, fill: '#222', fontWeight: 700 }}
              tickLine={false}
              axisLine={{ stroke: '#999' }}
              width={40}
            />
            {typeof national2022 === 'number' && (
              <ReferenceLine
                x={national2022}
                stroke="#111"
                strokeWidth={2.5}
                strokeDasharray="2 5"
                ifOverflow="hidden"
                label={{
                  value: `Nat. 2022 · ${national2022.toFixed(1)}%`,
                  position: 'top',
                  fill: '#111',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              />
            )}
            <Tooltip
              content={<AltTooltip waveLabel={waveLabel} />}
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            />

            {/* 2022 bar first — visually subordinate (gray fill). */}
            <Bar
              dataKey="t2022"
              name="2022"
              fill="#cccccc"
              isAnimationActive={false}
              radius={[0, 2, 2, 0]}
            />

            {/* 2026 bar — coloured by 2022 margin (same palette as the
                cubes + history chart) so the experiments share a
                visual language. */}
            <Bar
              dataKey="t2026"
              name="2026"
              isAnimationActive={false}
              radius={[0, 2, 2, 0]}
            >
              {rows.map((r) => (
                <Cell key={r.name} fill={r.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {!anyLive && (
        <p className="turnout-alt-bars__awaiting">
          Awaiting 2026 {waveLabel.toLowerCase()} figures — the live
          bars will appear here once the newsroom enters them.
        </p>
      )}
    </div>
  );
}

// ── Custom tooltip — shows both years + the delta if both are set.

interface TooltipPayloadEntry {
  dataKey?: string;
  value?: number | null;
  color?: string;
  payload?: ChartRow;
}

interface AltTooltipProps {
  active?: boolean;
  label?: string;
  payload?: TooltipPayloadEntry[];
  waveLabel: string;
}

function AltTooltip({ active, label, payload, waveLabel }: AltTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const delta =
    typeof row.t2026 === 'number' && typeof row.t2022 === 'number'
      ? row.t2026 - row.t2022
      : null;
  return (
    <div className="turnout-alt-bars__tooltip">
      <div className="turnout-alt-bars__tooltip-name">
        {label} · {waveLabel}
      </div>
      <div className="turnout-alt-bars__tooltip-row">
        <span
          className="turnout-alt-bars__tooltip-swatch"
          style={{ background: row.color }}
        />
        <span>2026</span>
        <span className="turnout-alt-bars__tooltip-value">
          {typeof row.t2026 === 'number' ? `${row.t2026.toFixed(1)}%` : '—'}
        </span>
      </div>
      <div className="turnout-alt-bars__tooltip-row">
        <span
          className="turnout-alt-bars__tooltip-swatch"
          style={{ background: '#cccccc' }}
        />
        <span>2022</span>
        <span className="turnout-alt-bars__tooltip-value">
          {typeof row.t2022 === 'number' ? `${row.t2022.toFixed(1)}%` : '—'}
        </span>
      </div>
      {delta !== null && (
        <div className="turnout-alt-bars__tooltip-delta">
          {delta > 0 ? '+' : ''}
          {delta.toFixed(1)} pp vs 2022
        </div>
      )}
    </div>
  );
}
